// Chapter helpers shared by the details page, the reader and the studio.

export const getChapterId = (chapter) => chapter?.id || chapter?.chapterId || chapter?.chapter_id;
export const getChapterNumber = (chapter) => chapter?.number ?? chapter?.chapterNumber ?? chapter?.index ?? chapter?.chapter_index;
export const getChapterTitle = (chapter) => chapter?.title || chapter?.name;
export const getChapterViews = (chapter) => chapter?.views ?? chapter?.viewCount ?? chapter?.viewsCount ?? chapter?.views_count;

const resolveVolumeValue = (value) => {
    if (value === null || value === undefined || typeof value !== 'object') {
        return value;
    }
    return value.number
        ?? value.volumeNumber
        ?? value.volume_number
        ?? value.index
        ?? value.volume
        ?? value.name
        ?? value.id;
};

export const getChapterVolume = (chapter) => resolveVolumeValue(
    chapter?.volume
    ?? chapter?.volumeNumber
    ?? chapter?.volume_number
    ?? chapter?.vol
    ?? chapter?.volume_index
    ?? chapter?.volumeIndex
    ?? chapter?.volumeId
    ?? chapter?.volume_id
    ?? chapter?.volumeNo
    ?? chapter?.volume_no
);

export const NO_VOLUME = 'no-volume';

export const getVolumeKey = (chapter) => {
    const volume = getChapterVolume(chapter);
    return volume !== null && volume !== undefined && volume !== '' ? String(volume) : NO_VOLUME;
};

export const hasValue = (value) => value !== null && value !== undefined && value !== '';

export const formatChapterLabel = (chapter) => {
    const number = getChapterNumber(chapter);
    return hasValue(number) ? `Глава ${number}` : 'Глава';
};

const normalizeChapterSortValue = (chapter) => {
    const raw = getChapterNumber(chapter);
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }
    const trimmed = String(raw).trim();
    if (!trimmed) {
        return null;
    }
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : trimmed.toLowerCase();
};

export const compareChaptersByNumber = (left, right) => {
    const leftValue = normalizeChapterSortValue(left);
    const rightValue = normalizeChapterSortValue(right);
    const leftIsNumber = typeof leftValue === 'number';
    const rightIsNumber = typeof rightValue === 'number';
    if (leftIsNumber && rightIsNumber) {
        return leftValue - rightValue;
    }
    if (leftIsNumber) {
        return -1;
    }
    if (rightIsNumber) {
        return 1;
    }
    if (leftValue && rightValue && leftValue !== rightValue) {
        return leftValue < rightValue ? -1 : 1;
    }
    return String(getChapterTitle(left) || '').localeCompare(String(getChapterTitle(right) || ''), 'ru');
};

export const sortChapters = (chapters) => [...(chapters || [])].sort(compareChaptersByNumber);

// Volume keys in reading order, "no volume" last.
export const collectVolumeKeys = (chapters) => {
    const keys = [];
    const seen = new Set();
    (chapters || []).forEach((chapter) => {
        const key = getVolumeKey(chapter);
        if (!seen.has(key)) {
            seen.add(key);
            keys.push(key);
        }
    });
    return keys.sort((a, b) => {
        if (a === NO_VOLUME) {
            return 1;
        }
        if (b === NO_VOLUME) {
            return -1;
        }
        const aNum = Number(a);
        const bNum = Number(b);
        if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
            return aNum - bNum;
        }
        return a.localeCompare(b, 'ru', { numeric: true });
    });
};

export const formatVolumeLabel = (key) => (key === NO_VOLUME ? 'Без тома' : `Том ${key}`);

// Last opened chapter per manga, kept in this browser only.
const progressKey = (mangaId) => `mangaProgress:${mangaId}`;

export const readMangaProgress = (mangaId) => {
    if (!mangaId) {
        return null;
    }
    try {
        const raw = localStorage.getItem(progressKey(mangaId));
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};

export const saveMangaProgress = (mangaId, chapterId, page = 0) => {
    if (!mangaId || !chapterId) {
        return;
    }
    try {
        localStorage.setItem(progressKey(mangaId), JSON.stringify({ chapterId: String(chapterId), page, at: Date.now() }));
    } catch (error) {
        // Private mode or full storage: progress is a convenience only.
    }
};
